import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  Stack,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import { useTranslation } from "react-i18next";
import { useLanguage } from "../../context/languageContext";
import { useAuth } from "../../context/authContext";

interface HelpModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Modal d'aide affichant un guide utilisateur PDF et une vidéo de présentation
 */
const HelpModal: React.FC<HelpModalProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const { language, setLanguage } = useLanguage();
  const { user } = useAuth();

  const handleLanguageChange = (
    _event: React.MouseEvent<HTMLElement>,
    newLanguage: "fr" | "en" | null
  ) => {
    if (newLanguage !== null) {
      setLanguage(newLanguage);
    }
  };

  const handleDownloadPDF = () => {
    const roleFolder = user?.role === "User" ? "user" : "admin";
    window.open(`/docs/${roleFolder}/${language}/GUIDE.pdf`, "_blank");
  };

  const getVideoSrc = () => {
    const roleFolder = user?.role === "User" ? "user" : "admin";
    return `/videos/${roleFolder}/${language}/DEMO.mp4`;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: "500px",
        },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: "flex",
          alignItems: "center",
          backgroundColor: "primary.main",
        }}
      >
        <Typography
          variant="h5"
          component="span"
          sx={{ flexGrow: 1, color: "white" }}
        >
          {t("help.title")}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <ToggleButtonGroup
            value={language}
            exclusive
            onChange={handleLanguageChange}
            size="small"
            sx={{
              "& .MuiToggleButton-root": {
                color: "white",
                backgroundColor: "transparent",
                borderColor: "rgba(255, 255, 255, 0.5)",
                minWidth: "50px",
                px: 2,
                "&.Mui-selected": {
                  backgroundColor: "white",
                  color: "primary.main",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                  },
                },
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                },
              },
            }}
          >
            <ToggleButton value="fr">FR</ToggleButton>
            <ToggleButton value="en">EN</ToggleButton>
          </ToggleButtonGroup>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              color: "white",
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={4}>
          {/* Section Guide PDF */}
          <Box>
            <Box display="flex" alignItems="center" mb={2}>
              <PictureAsPdfIcon color="error" sx={{ mr: 1, fontSize: 28 }} />
              <Typography variant="h6">{t("help.pdfSection.title")}</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" mb={2}>
              {t("help.pdfSection.description")}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<PictureAsPdfIcon />}
              onClick={handleDownloadPDF}
            >
              {t("help.pdfSection.button")}
            </Button>
          </Box>

          <Divider />

          {/* Section Vidéo */}
          <Box>
            <Box display="flex" alignItems="center" mb={2}>
              <PlayCircleOutlineIcon
                color="primary"
                sx={{ mr: 1, fontSize: 28 }}
              />
              <Typography variant="h6">{t("help.videoSection.title")}</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" mb={2}>
              {t("help.videoSection.description")}
            </Typography>
            <Box
              sx={{
                position: "relative",
                paddingBottom: "56.25%", // 16:9 aspect ratio
                height: 0,
                overflow: "hidden",
                borderRadius: 1,
                backgroundColor: "#000",
              }}
            >
              <video
                key={`${language}-${user?.role}`}
                controls
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                }}
              >
                <source src={getVideoSrc()} type="video/mp4" />
                {t("help.videoSection.noSupport")}
              </video>
            </Box>
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default HelpModal;
